import { TextInput, TextAreaInput, DateInput, Select } from '@octano/global-ui';
import { Row, Col, Container } from 'reactstrap';
import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { TranslationsKeys } from '../../../locales/translations';
import {
  AnnouncementFormFields,
  AnnouncementFormFile,
  AnnouncementSelectOptionType,
  AnnouncementTarget,
} from '../types';
import AzureFilesUploader from '../../../components/AzureFilesUploader/AzureFilesUploader';
import AnnouncementChip from './AnnouncementChip';
import { convertToFormFile } from '../utils';
import { useValidations } from '../../../hooks/useValidations';
import { useAnnouncementModalLoader } from './Loaders/AnnouncementModalLoader';
import TooltipForSelect from '../../../components/tooltip/TooltipForSelect';

const AnnouncementEditForm = () => {
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_ANNOUNCEMENTS_MODAL,
  );
  const { msgValidations } = useValidations();
  const { data: dataRaw } = useAnnouncementModalLoader();

  const [filesState, setFilesState] = useState<number>(0);
  const [targetsState, setTargetsState] = useState<number>(0);

  const { control, watch } = useFormContext<AnnouncementFormFields>();
  const supplementaryFiles = useFieldArray({
    control,
    name: 'supplementaryFiles',
  });
  const targetsItems = useFieldArray({
    control,
    name: 'targets' as any,
  });

  const [
    targets,
    files,
    academicLevelValue,
    schoolValue,
    studyPlanValue,
  ] = watch([
    'targets',
    'supplementaryFiles',
    'academicLevel',
    'school',
    'studyPlan',
  ]);

  const targetsOptions = useMemo<AnnouncementSelectOptionType[]>(
    () =>
      Object.values(AnnouncementTarget).map((target) => ({
        label: t(`targets.${target}`),
        value: target,
      })),
    [t],
  );

  const data = useMemo(() => {
    const nextData = { ...dataRaw };
    if (academicLevelValue?.value && schoolValue?.value) {
      nextData.studyPlans =
        nextData.studyPlans?.filter(
          (e) =>
            e.extra?.schoolId === schoolValue?.value &&
            e.extra?.academicLevelId === academicLevelValue?.value,
        ) || [];
    } else {
      nextData.studyPlans = [];
    }

    return nextData;
  }, [dataRaw, academicLevelValue?.value, schoolValue?.value]);

  const handleAddTarget = useCallback(
    (target: AnnouncementTarget) => {
      const currentTargets = [...(targets ?? [])];
      if (!currentTargets.includes(target)) {
        targetsItems.append(target);
      }
      setTargetsState((prev) => prev + 1);
    },
    [targets, targetsItems],
  );
  const handleRemoveTarget = useCallback(
    (index: number) => {
      targetsItems.remove(index);
      setTargetsState((prev) => prev + 1);
    },
    [targetsItems],
  );

  const handleAddFile = useCallback(
    (file: AnnouncementFormFile) => {
      supplementaryFiles.append(file);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );
  const handleRemoveFile = useCallback(
    (index: number) => {
      supplementaryFiles.remove(index);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );

  return (
    <Container>
      <Row>
        <Col xs={12}>
          <TextInput
            control={control}
            name="title"
            label={t('fields.title')}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col className="mb-4" xs={12}>
          <TextAreaInput
            control={control}
            name="description"
            label={t('fields.description')}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} md={6}>
          <DateInput
            control={control}
            name="publishAt"
            label={t('fields.publishAt')}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} md={6}>
          <DateInput
            control={control}
            name="validUntilAt"
            label={t('fields.validUntilAt')}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col key={targetsState.toString()} xs={12}>
          <p className="text-left w-100 fs-16">{t(`fields.targetsNotice`)}</p>
          <Select
            name="targets"
            label={t('fields.targets')}
            value={null as any}
            options={targetsOptions}
            onChange={(e) => handleAddTarget(e.value)}
          />
          {!!targets?.length && (
            <div
              className="w-full mb-4 d-flex flex-wrap align-items-center justify-content-start"
              style={{ gap: 5 }}
            >
              {targets?.map((target, i) => (
                <AnnouncementChip
                  key={i.toString()}
                  value={target}
                  children={t(`targets.${target}`)}
                  onRemove={() => handleRemoveTarget(i)}
                />
              ))}
            </div>
          )}
        </Col>
        {(targets.includes(AnnouncementTarget.Students) ||
          targets.includes(AnnouncementTarget.Teachers)) && (
          <Col xs={12} md={6}>
            <TooltipForSelect
              isClearable={true}
              hasValue={!!schoolValue?.value}
              id="AnnouncementSchool"
              text={t(`tooltips.school`)}
            >
              <Select
                control={control}
                name="school"
                options={data?.schools || []}
                label={t('terms:school.single')}
                isClearable={true}
              />
            </TooltipForSelect>
          </Col>
        )}
        {targets.includes(AnnouncementTarget.Students) && (
          <Col xs={12} md={6}>
            <TooltipForSelect
              isClearable={true}
              hasValue={!!academicLevelValue?.value}
              id="AnnouncementAcademicLevel"
              text={t(`tooltips.academicLevel`)}
            >
              <Select
                control={control}
                name="academicLevel"
                options={data?.academicLevels || []}
                label={t('terms:academicLevel.single')}
                isClearable={true}
              />
            </TooltipForSelect>
          </Col>
        )}
        {!!(
          targets.includes(AnnouncementTarget.Students) &&
          data?.studyPlans?.length
        ) && (
          <Col xs={12} md={6}>
            <TooltipForSelect
              isClearable={true}
              hasValue={!!studyPlanValue?.value}
              id="AnnouncementStudyPlan"
              text={t(`tooltips.studyPlan`)}
            >
              <Select
                control={control}
                name="studyPlan"
                options={data?.studyPlans || []}
                label={t('terms:studyPlan.single')}
                isClearable={true}
              />
            </TooltipForSelect>
          </Col>
        )}
        <Col className="mt-3" xs={12}>
          <div
            className="w-full p-4 rounded"
            style={{
              boxShadow: '0px 0px 10px 2px rgba(17,17,26,0.08)',
            }}
            key={filesState.toString()}
          >
            <AzureFilesUploader
              label={t('fields.files')}
              onUploaded={(next) => handleAddFile(convertToFormFile(next))}
            />
            {!!files?.length && (
              <div
                className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
                style={{ gap: 5 }}
              >
                {files?.map((file, i) => (
                  <AnnouncementChip
                    key={file.id.toString()}
                    value={i}
                    children={file.name}
                    onRemove={handleRemoveFile}
                  />
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AnnouncementEditForm;
